import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Layout/Header';
import Sidebar from '../Layout/Sidebar';
import RouteSelector from '../Dashboard/RouteSelector';
import DateRangeFilter from '../Dashboard/DateRangeFilter';

// Mapping of airport codes to IANA timezone names
const airportTimezones = {
  'HND': 'Asia/Tokyo', // Tokyo Haneda, Japan (UTC+09:00)
  'NRT': 'Asia/Tokyo', // Tokyo Narita, Japan (UTC+09:00)
  'KIX': 'Asia/Tokyo', // Osaka, Japan (UTC+09:00)
  'NGO': 'Asia/Tokyo', // Nagoya, Japan (UTC+09:00)
  'HNL': 'Pacific/Honolulu', // Honolulu, Hawaii (UTC-10:00)
  'JFK': 'America/New_York', // New York JFK (UTC-05:00/-04:00 with DST)
  'BOS': 'America/New_York', // Boston (UTC-05:00/-04:00 with DST)
  'ORD': 'America/Chicago', // Chicago (UTC-06:00/-05:00 with DST)
  'DFW': 'America/Dallas', // Dallas (UTC-06:00/-05:00 with DST)
  'LAX': 'America/Los_Angeles', // Los Angeles (UTC-08:00/-07:00 with DST)
  'SAN': 'America/Los_Angeles', // San Diego (UTC-08:00/-07:00 with DST)
  'SFO': 'America/Los_Angeles', // San Francisco (UTC-08:00/-07:00 with DST)
  'SEA': 'America/Los_Angeles', // Seattle (UTC-08:00/-07:00 with DST)
  'YVR': 'America/Los_Angeles', // Vancouver (UTC-08:00/-07:00 with DST)
};

const CalendarPage = () => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [displayMonths, setDisplayMonths] = useState(6);
  
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const start = new Date();
    const end = new Date(start);
    end.setMonth(start.getMonth() + 6); // Initial endDate based on displayMonths
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    };
  });
  
  const formatDateForGrouping = (timeStr, origin) => {
    if (!timeStr || !origin) return '';
    const timezone = airportTimezones[origin] || 'UTC';
    const date = new Date(timeStr);
    // Convert the date to the origin's timezone and format as YYYY-MM-DD
    return new Date(date.toLocaleString('en-US', { timeZone: timezone }))
      .toISOString()
      .split('T')[0];
  };
  
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
    
    fetch('/api/auth/verify', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(res => res.json())
    .then(data => {
      if (!data.authenticated) {
        localStorage.removeItem('authToken');
        navigate('/login');
      }
    })
    .catch(err => {
      console.error('Auth verification error:', err);
      navigate('/login');
    });

    const fetchRoutes = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch('/api/flights/routes', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch routes');
        
        const data = await response.json();
        setRoutes(data);
        
        if (data.length > 0) {
          setSelectedRoute({
            origin: data[0].origin,
            destination: data[0].destination
          });
        }
      } catch (err) {
        setError('Failed to load routes. Please try again.');
        console.error('Route fetch error:', err);
      }
    };

    fetchRoutes();
  }, [navigate]);
  
  useEffect(() => {
    if (!selectedRoute) return;

    const fetchFlights = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const { origin, destination } = selectedRoute;
        const { startDate, endDate } = dateRange;
        
        const url = new URL('/api/flights/flights', window.location.origin);
        url.searchParams.append('origin', origin);
        url.searchParams.append('destination', destination);
        url.searchParams.append('startDate', startDate);
        url.searchParams.append('endDate', endDate);
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch flights');
        
        const data = await response.json();
        setFlights(data);
      } catch (err) {
        setError('Failed to load flights. Please try again.');
        console.error('Flight fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFlights();
  }, [selectedRoute, dateRange]);
  
  useEffect(() => {
    // Update the endDate in dateRange based on displayMonths
    const newEndDate = new Date(dateRange.startDate);
    newEndDate.setMonth(newEndDate.getMonth() + displayMonths);
    setDateRange(prev => ({
      ...prev,
      endDate: newEndDate.toISOString().split('T')[0]
    }));
  }, [displayMonths, dateRange.startDate]);
  
  const handleRouteChange = (route) => {
    setSelectedRoute(route);
  };
  
  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };
  
  const groupFlightsByDate = () => {
    const grouped = {};
    
    flights.forEach(flight => {
      const date = formatDateForGrouping(flight.depart_time, flight.origin);
      
      if (!grouped[date]) {
        grouped[date] = {
          minPoints: flight.points,
          count: 1,
          flights: [flight]
        };
      } else {
        grouped[date].minPoints = Math.min(grouped[date].minPoints, flight.points);
        grouped[date].count += 1;
        grouped[date].flights.push(flight);
      }
    });
    
    return grouped;
  };
  
  const getPointsColorClass = (points) => {
    if (!points) return 'bg-gray-100 text-gray-500';
    
    if (points <= 10000) return 'bg-green-100 text-green-800';
    if (points <= 20000) return 'bg-blue-100 text-blue-800';
    if (points <= 30000) return 'bg-yellow-100 text-yellow-800';
    if (points <= 50000) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };
  
  const generateCalendarMonths = () => {
    if (!dateRange.startDate || !dateRange.endDate) return [];
    
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    const months = [];
    
    const currentDate = new Date(start.getFullYear(), start.getMonth(), 1);
    let monthCount = 0;
    
    while (currentDate <= end && monthCount < displayMonths) {
      months.push({
        year: currentDate.getFullYear(),
        month: currentDate.getMonth(),
        name: currentDate.toLocaleString('default', { month: 'long' }),
        days: generateDaysForMonth(currentDate.getFullYear(), currentDate.getMonth())
      });
      
      currentDate.setMonth(currentDate.getMonth() + 1);
      monthCount++;
    }
    
    return months;
  };
  
  const generateDaysForMonth = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const firstDayOfWeek = firstDay.getDay();
    
    const days = [];
    const groupedFlights = groupFlightsByDate();
    
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ empty: true });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0];
      const dayData = groupedFlights[dateStr];
      
      days.push({
        day,
        date,
        dateStr,
        flightData: dayData,
        isToday: dateStr === new Date().toISOString().split('T')[0]
      });
    }
    
    return days;
  };
  
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };
  
  const months = generateCalendarMonths();
  
  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onLogout={handleLogout} />
        <main className="flex-1 overflow-y-auto p-4">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-4">
                Award Flight Calendar
              </h1>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <RouteSelector 
                  routes={routes} 
                  selectedRoute={selectedRoute} 
                  onChange={handleRouteChange} 
                />
                <DateRangeFilter 
                  dateRange={dateRange} 
                  onChange={handleDateRangeChange} 
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Months to Display
                </label>
                <select
                  value={displayMonths}
                  onChange={(e) => setDisplayMonths(parseInt(e.target.value))}
                  className="w-32 block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value={3}>3 Months</option>
                  <option value={6}>6 Months</option>
                  <option value={9}>9 Months</option>
                  <option value={12}>12 Months</option>
                </select>
              </div>
              {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                  {error}
                </div>
              )}
            </div>
            {loading ? (
              <div className="bg-white rounded-lg shadow p-6 flex justify-center items-center h-64">
                <div className="text-gray-500 flex items-center">
                  <svg className="animate-spin h-5 w-5 mr-3 text-blue-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading calendar data...
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                {months.map((month) => (
                  <div key={`${month.year}-${month.month}`} className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">
                      {month.name} {month.year}
                    </h2>
                    <div className="grid grid-cols-7 gap-1">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                        <div 
                          key={day} 
                          className="text-xs font-medium text-gray-500 text-center p-2"
                        >
                          {day}
                        </div>
                      ))}
                      {month.days.map((day, index) => {
                        if (day.empty) {
                          return <div key={`empty-${index}`} className="h-16 bg-gray-50 rounded-md" />;
                        }
                        const colorClass = day.flightData 
                          ? getPointsColorClass(day.flightData.minPoints)
                          : 'bg-gray-50 text-gray-400';
                        return (
                          <div 
                            key={day.dateStr} 
                            className={`h-16 rounded-md border ${day.isToday ? 'border-blue-500' : 'border-gray-200'} overflow-hidden`}
                          >
                            <div className="flex flex-col h-full">
                              <div className="text-xs font-medium p-1 text-right text-gray-700">
                                {day.day}
                              </div>
                              {day.flightData ? (
                                <div className={`flex-1 flex items-center justify-center ${colorClass} text-xs p-1`}>
                                  <div className="text-center">
                                    <div className="font-semibold">{day.flightData.minPoints.toLocaleString()}</div>
                                    <div>{day.flightData.count} {day.flightData.count === 1 ? 'flight' : 'flights'}</div>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex-1 flex items-center justify-center bg-gray-50 text-xs text-gray-400">
                                  No data
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-3">Points Legend</h3>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center">
                      <span className="w-6 h-6 inline-block bg-green-100 rounded mr-2"></span>
                      <span className="text-sm text-gray-600">= 10,000 points</span>
                    </div>
                    <div className="flex items-center">
                      <span className="w-6 h-6 inline-block bg-blue-100 rounded mr-2"></span>
                      <span className="text-sm text-gray-600">= 20,000 points</span>
                    </div>
                    <div className="flex items-center">
                      <span className="w-6 h-6 inline-block bg-yellow-100 rounded mr-2"></span>
                      <span className="text-sm text-gray-600">= 30,000 points</span>
                    </div>
                    <div className="flex items-center">
                      <span className="w-6 h-6 inline-block bg-orange-100 rounded mr-2"></span>
                      <span className="text-sm text-gray-600">= 50,000 points</span>
                    </div>
                    <div className="flex items-center">
                      <span className="w-6 h-6 inline-block bg-red-100 rounded mr-2"></span>
                      <span className="text-sm text-gray-600">> 50,000 points</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CalendarPage;